/**
 * InfinitSales - Esqueci minha senha (2026-10-14)
 *
 * Dois passos: e-mail → código de 6 dígitos + senha nova. O código chega por
 * e-mail (EmailJS) e vale 15 minutos; o backend devolve um reset_token que
 * acompanha o código (sem estado no servidor). Só existe com
 * auth-config.password_reset_enabled; desligado, a tela avisa e volta ao login.
 *
 * Mesmo visual de Login/Register (branding à esquerda, formulário à direita).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, ArrowRight, ArrowLeft, Loader2, Check, KeyRound } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import authService from '../../services/global/authService';

const RESEND_COOLDOWN_SECONDS = 60;

const errorText = (err, fallback) => {
  const detail = err?.response?.data?.detail;
  return typeof detail === 'string' && detail ? detail : fallback;
};

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { colors } = useTheme();

  const [config, setConfig] = useState(null);
  const [step, setStep] = useState('email'); // 'email' | 'code'
  const [email, setEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [focusedField, setFocusedField] = useState(null);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    authService.getAuthConfig().then(setConfig);
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const requestCode = async () => {
    const response = await authService.forgotPassword(email.trim());
    setResetToken(response.reset_token);
    setCooldown(RESEND_COOLDOWN_SECONDS);
    return response;
  };

  const handleSendCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await requestCode();
      setCode('');
      setInfo(`Se ${email.trim()} tiver uma conta, o código chega em instantes. Confira também o spam.`);
      setStep('code');
    } catch (err) {
      setError(errorText(err, 'Não foi possível enviar o código agora. Tente de novo em instantes.'));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0 || loading) return;
    setLoading(true);
    setError('');
    try {
      await requestCode();
      setCode('');
      setInfo('Enviamos um código novo. Use sempre o último que chegar.');
    } catch (err) {
      setError(errorText(err, 'Não foi possível reenviar o código agora.'));
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setError('');
    if (code.length !== 6) {
      setError('Digite os 6 dígitos do código.');
      return;
    }
    if (password.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres');
      return;
    }
    if (password !== confirmPassword) {
      setError('As senhas não coincidem');
      return;
    }
    setLoading(true);
    try {
      await authService.resetPassword(resetToken, code, password);
      navigate('/login', {
        replace: true,
        state: { notice: 'Senha redefinida. Agora é só entrar com a senha nova.', email: email.trim() },
      });
    } catch (err) {
      setError(errorText(err, 'Não foi possível redefinir a senha. Tente de novo.'));
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: {
      display: 'flex',
      minHeight: '100vh',
      backgroundColor: colors.bgSecondary,
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif",
    },
    branding: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      padding: '48px',
      backgroundColor: colors.bgPrimary,
      borderRight: `1px solid ${colors.borderColor}`,
      position: 'relative',
      overflow: 'hidden',
    },
    brandingBg: {
      position: 'absolute',
      inset: 0,
      background: `radial-gradient(circle at 30% 20%, ${colors.accentPrimary}15 0%, transparent 50%)`,
      pointerEvents: 'none',
    },
    brandingContent: { position: 'relative', zIndex: 1, maxWidth: '480px' },
    logo: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '48px', cursor: 'pointer' },
    logoIcon: {
      width: '48px',
      height: '48px',
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: `1px solid ${colors.borderColor}`,
      overflow: 'hidden',
    },
    logoText: { fontSize: '24px', fontWeight: 700, color: colors.textPrimary },
    title: { fontSize: '42px', fontWeight: 700, lineHeight: 1.2, marginBottom: '24px', color: colors.textPrimary },
    titleGradient: {
      background: `linear-gradient(135deg, ${colors.accentPrimary} 0%, #34d399 100%)`,
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
    },
    description: { fontSize: '18px', lineHeight: 1.6, color: colors.textSecondary, marginBottom: '48px' },
    checklist: { display: 'flex', flexDirection: 'column', gap: '16px' },
    checkItem: { display: 'flex', alignItems: 'center', gap: '14px', fontSize: '15px', color: colors.textPrimary },
    checkIcon: {
      width: '24px',
      height: '24px',
      backgroundColor: `${colors.accentPrimary}20`,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: colors.accentPrimary,
      flexShrink: 0,
    },
    formContainer: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px' },
    formWrapper: { width: '100%', maxWidth: '400px' },
    formHeader: { marginBottom: '32px', textAlign: 'center' },
    formTitle: { fontSize: '28px', fontWeight: 700, color: colors.textPrimary, marginBottom: '8px' },
    formSubtitle: { fontSize: '15px', color: colors.textSecondary, lineHeight: 1.5 },
    error: {
      padding: '12px 16px',
      backgroundColor: 'rgba(239, 68, 68, 0.1)',
      border: '1px solid rgba(239, 68, 68, 0.3)',
      borderRadius: '8px',
      color: '#ef4444',
      fontSize: '14px',
      marginBottom: '24px',
      textAlign: 'center',
    },
    info: {
      padding: '12px 16px',
      backgroundColor: `${colors.accentPrimary}12`,
      border: `1px solid ${colors.accentPrimary}40`,
      borderRadius: '8px',
      color: colors.textPrimary,
      fontSize: '14px',
      lineHeight: 1.5,
      marginBottom: '24px',
      textAlign: 'center',
    },
    form: { display: 'flex', flexDirection: 'column', gap: '20px' },
    inputGroup: { display: 'flex', flexDirection: 'column', gap: '8px' },
    label: { fontSize: '14px', fontWeight: 500, color: colors.textPrimary },
    inputWrapper: { position: 'relative', display: 'flex', alignItems: 'center' },
    inputIcon: { position: 'absolute', left: '14px', color: colors.textTertiary, pointerEvents: 'none', transition: 'color 0.2s' },
    input: {
      width: '100%',
      padding: '14px 14px 14px 46px',
      backgroundColor: colors.bgPrimary,
      border: `2px solid ${colors.borderColor}`,
      borderRadius: '10px',
      fontSize: '15px',
      color: colors.textPrimary,
      outline: 'none',
      transition: 'border-color 0.2s, box-shadow 0.2s',
    },
    codeInput: {
      fontSize: '22px',
      fontWeight: 600,
      letterSpacing: '0.5em',
      fontVariantNumeric: 'tabular-nums',
    },
    inputFocused: { borderColor: colors.accentPrimary, boxShadow: `0 0 0 3px ${colors.accentPrimary}20` },
    inputAction: {
      position: 'absolute',
      right: '12px',
      padding: '6px',
      border: 'none',
      background: 'transparent',
      color: colors.textTertiary,
      cursor: 'pointer',
      borderRadius: '6px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    submitBtn: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      padding: '14px 24px',
      backgroundColor: colors.accentPrimary,
      color: 'white',
      border: 'none',
      borderRadius: '10px',
      fontSize: '15px',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'background-color 0.2s, transform 0.1s',
      marginTop: '8px',
    },
    submitBtnDisabled: { opacity: 0.7, cursor: 'not-allowed' },
    textBtn: {
      background: 'transparent',
      border: 'none',
      padding: 0,
      color: colors.accentPrimary,
      fontSize: '14px',
      fontWeight: 500,
      cursor: 'pointer',
    },
    footer: { marginTop: '32px', textAlign: 'center', fontSize: '14px', color: colors.textSecondary },
    link: { color: colors.accentPrimary, textDecoration: 'none', fontWeight: 500 },
  };

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 900;
  if (isMobile) {
    styles.branding.display = 'none';
    styles.formContainer.padding = '24px';
  }

  const inputStyle = (field, extra = {}) => ({
    ...styles.input,
    ...extra,
    ...(focusedField === field ? styles.inputFocused : {}),
  });
  const iconStyle = (field) => ({
    ...styles.inputIcon,
    color: focusedField === field ? colors.accentPrimary : colors.textTertiary,
  });

  const logo = (
    <Link to="/home" style={{ textDecoration: 'none' }}>
      <div style={{ ...styles.logo, ...(isMobile ? { justifyContent: 'center', marginBottom: '32px' } : {}) }}>
        <div style={styles.logoIcon}>
          <img src={process.env.PUBLIC_URL + '/logo.jpeg'} alt="InfinitSales" style={{ width: '34px', height: '34px', objectFit: 'contain' }} />
        </div>
        <span style={styles.logoText}>InfinitSales</span>
      </div>
    </Link>
  );

  const unavailable = config && !config.password_reset_enabled;

  return (
    <div style={styles.container}>
      {/* Left Side - Branding */}
      <div style={styles.branding}>
        <div style={styles.brandingBg} />
        <div style={styles.brandingContent}>
          {logo}
          <h1 style={styles.title}>
            Recupere seu acesso
            <br />
            <span style={styles.titleGradient}>em poucos passos</span>
          </h1>
          <p style={styles.description}>
            Enviamos um código para o seu e-mail. Com ele você cria uma senha nova
            e volta a atender seus clientes.
          </p>
          <div style={styles.checklist}>
            {[
              'Código de 6 dígitos válido por 15 minutos',
              'Sua senha atual continua valendo até você trocar',
              'Avisamos por e-mail quando a senha for alterada',
            ].map((item) => (
              <div key={item} style={styles.checkItem}>
                <div style={styles.checkIcon}>
                  <Check size={14} />
                </div>
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div style={styles.formContainer}>
        <div style={styles.formWrapper}>
          {isMobile && logo}

          {!config ? (
            <div style={{ display: 'flex', justifyContent: 'center', color: colors.textSecondary }}>
              <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
            </div>
          ) : unavailable ? (
            <>
              <div style={styles.formHeader}>
                <h2 style={styles.formTitle}>Esqueci minha senha</h2>
                <p style={styles.formSubtitle}>
                  A recuperação de senha por e-mail ainda não está disponível.
                  Fale com o administrador da sua empresa ou com o nosso suporte.
                </p>
              </div>
              <button type="button" style={styles.submitBtn} onClick={() => navigate('/login')}>
                <ArrowLeft size={18} />
                Voltar ao login
              </button>
            </>
          ) : step === 'email' ? (
            <>
              <div style={styles.formHeader}>
                <h2 style={styles.formTitle}>Esqueci minha senha</h2>
                <p style={styles.formSubtitle}>Informe o e-mail da sua conta e enviaremos um código</p>
              </div>

              {error && <div style={styles.error}>{error}</div>}

              <form onSubmit={handleSendCode} style={styles.form}>
                <div style={styles.inputGroup}>
                  <label style={styles.label} htmlFor="email">E-mail</label>
                  <div style={styles.inputWrapper}>
                    <Mail size={18} style={iconStyle('email')} />
                    <input
                      type="email"
                      id="email"
                      name="email"
                      placeholder="seu@email.com"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); setError(''); }}
                      onFocus={() => setFocusedField('email')}
                      onBlur={() => setFocusedField(null)}
                      required
                      autoComplete="email"
                      autoFocus
                      style={inputStyle('email')}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  style={{ ...styles.submitBtn, ...(loading ? styles.submitBtnDisabled : {}) }}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                      Enviando...
                    </>
                  ) : (
                    <>
                      Enviar código
                      <ArrowRight size={18} />
                    </>
                  )}
                </button>
              </form>
            </>
          ) : (
            <>
              <div style={styles.formHeader}>
                <h2 style={styles.formTitle}>Crie uma senha nova</h2>
                <p style={styles.formSubtitle}>Digite o código que enviamos e escolha a senha nova</p>
              </div>

              {error ? <div style={styles.error}>{error}</div> : info && <div style={styles.info}>{info}</div>}

              <form onSubmit={handleReset} style={styles.form}>
                <div style={styles.inputGroup}>
                  <label style={styles.label} htmlFor="code">Código de 6 dígitos</label>
                  <div style={styles.inputWrapper}>
                    <KeyRound size={18} style={iconStyle('code')} />
                    <input
                      type="text"
                      id="code"
                      name="code"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      placeholder="000000"
                      maxLength={6}
                      value={code}
                      onChange={(e) => { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
                      onFocus={() => setFocusedField('code')}
                      onBlur={() => setFocusedField(null)}
                      required
                      autoFocus
                      style={inputStyle('code', styles.codeInput)}
                    />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: colors.textSecondary }}>
                    <button type="button" style={styles.textBtn} onClick={() => { setStep('email'); setError(''); setInfo(''); }}>
                      Usar outro e-mail
                    </button>
                    <button
                      type="button"
                      style={{ ...styles.textBtn, ...(cooldown > 0 ? { color: colors.textTertiary, cursor: 'default' } : {}) }}
                      onClick={handleResend}
                      disabled={cooldown > 0 || loading}
                    >
                      {cooldown > 0 ? `Reenviar código em ${cooldown}s` : 'Reenviar código'}
                    </button>
                  </div>
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label} htmlFor="password">Senha nova</label>
                  <div style={styles.inputWrapper}>
                    <Lock size={18} style={iconStyle('password')} />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      name="password"
                      placeholder="mín. 6 caracteres"
                      value={password}
                      onChange={(e) => { setPassword(e.target.value); setError(''); }}
                      onFocus={() => setFocusedField('password')}
                      onBlur={() => setFocusedField(null)}
                      required
                      minLength={6}
                      autoComplete="new-password"
                      style={inputStyle('password', { paddingRight: '46px' })}
                    />
                    <button type="button" style={styles.inputAction} onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label} htmlFor="confirmPassword">Confirmar senha nova</label>
                  <div style={styles.inputWrapper}>
                    <Lock size={18} style={iconStyle('confirmPassword')} />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="confirmPassword"
                      name="confirmPassword"
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                      onFocus={() => setFocusedField('confirmPassword')}
                      onBlur={() => setFocusedField(null)}
                      required
                      minLength={6}
                      autoComplete="new-password"
                      style={inputStyle('confirmPassword')}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  style={{ ...styles.submitBtn, ...(loading ? styles.submitBtnDisabled : {}) }}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                      Salvando...
                    </>
                  ) : (
                    <>
                      Salvar senha nova
                      <ArrowRight size={18} />
                    </>
                  )}
                </button>
              </form>
            </>
          )}

          <div style={styles.footer}>
            <p>
              Lembrou a senha?{' '}
              <Link to="/login" style={styles.link}>
                Fazer login
              </Link>
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        input::placeholder {
          color: ${colors.textTertiary};
          letter-spacing: normal;
        }
      `}</style>
    </div>
  );
};

export default ForgotPassword;
